import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import {
  ScraperJsConfig,
  ScraperSelector,
  SourceItem,
  JsExtractorPreview,
} from '../../../../models';
import { updateSource, previewSelectors } from '../../services/sourceService';
import ScraperConfigFields from './ScraperConfigFields';
import TestUrlDialog from './TestUrlDialog';
import { Alert, Button, Card, Dialog, Spinner } from '../ui';
import { ClipboardPaste, Code, Copy } from '../ui/icons';

interface ScraperConfigFormProps {
  sourceId: string;
  source: SourceItem;
  onSave: () => void;
}

interface StoredPreviewPayload {
  sourceUrl: string;
  html: string;
  propertyData?: Record<string, any>;
  jsExtractorResults?: JsExtractorPreview[];
}

const ScraperConfigForm: React.FC<ScraperConfigFormProps> = ({
  sourceId,
  source,
  onSave,
}) => {
  const [selectors, setSelectors] = useState<Record<string, any>>(
    source.selectors || {}
  );
  const [jsConfigs, setJsConfigs] = useState<ScraperJsConfig[]>(
    source.js || []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonConfig, setJsonConfig] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewValues, setPreviewValues] = useState<Record<string, any>>({});
  const [jsExtractorResults, setJsExtractorResults] = useState<
    JsExtractorPreview[]
  >([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewUrlInput, setPreviewUrlInput] = useState('');
  const [previewLoadLoading, setPreviewLoadLoading] = useState(false);
  const [previewLoadError, setPreviewLoadError] = useState<string | null>(null);
  const previewRequestRef = useRef(0);
  const selectorsRef = useRef(selectors);
  const jsConfigsRef = useRef(jsConfigs);
  const [previewRefreshToken, setPreviewRefreshToken] = useState(0);
  const [cachedAIData, setCachedAIData] = useState<any>(null);
  const [lastAIContentField, setLastAIContentField] = useState<string>('');
  const previewStorageKey = useMemo(
    () => `scraper-preview-${sourceId}`,
    [sourceId]
  );
  const normalizedSourceDomain = useMemo(() => {
    if (!source.domain) {
      return null;
    }
    return source.domain
      .replace(/^https?:\/\//i, '')
      .replace(/\/.*$/, '')
      .toLowerCase();
  }, [source.domain]);

  const persistPreview = useCallback(
    (payload: StoredPreviewPayload) => {
      if (typeof window === 'undefined') return;
      sessionStorage.setItem(previewStorageKey, JSON.stringify(payload));
    },
    [previewStorageKey]
  );

  const clearPersistedPreview = useCallback(() => {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(previewStorageKey);
  }, [previewStorageKey]);

  // Memoize the onSelectorsChange function to prevent unnecessary re-renders
  const handleSelectorsChange = useCallback(
    (newSelectors: Record<string, any>) => {
      setSelectors(newSelectors);
      
      // Clear cached AI data if aiTextContent field changed
      const currentAIContent = newSelectors.aiTextContent?.selector || '';
      if (currentAIContent !== lastAIContentField) {
        setCachedAIData(null);
        setLastAIContentField(currentAIContent);
      }
    },
    [lastAIContentField]
  );

  const handleJsConfigsChange = useCallback((configs: ScraperJsConfig[]) => {
    setJsConfigs(configs);
  }, []);

  useEffect(() => {
    selectorsRef.current = selectors;
  }, [selectors]);

  useEffect(() => {
    jsConfigsRef.current = jsConfigs;
  }, [jsConfigs]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedRaw = sessionStorage.getItem(previewStorageKey);
    if (!storedRaw) {
      return;
    }
    try {
      const stored: StoredPreviewPayload = JSON.parse(storedRaw);
      if (!stored.html) return;
      setPreviewHtml(stored.html);
      setPreviewUrl(stored.sourceUrl || null);
      setPreviewUrlInput(stored.sourceUrl || '');
      setPreviewValues(stored.propertyData || {});
      setJsExtractorResults(stored.jsExtractorResults || []);
      setPreviewError(null);
    } catch (error) {
      console.error('Failed to restore preview cache', error);
    }
  }, [previewStorageKey]);

  const handlePreviewUrlInputChange = useCallback(
    (value: string) => {
      setPreviewUrlInput(value);
      if (previewLoadError) {
        setPreviewLoadError(null);
      }
    },
    [previewLoadError]
  );

  const handlePreviewReady = useCallback(
    (payload: {
      html?: string;
      sourceUrl: string;
      propertyData?: Record<string, any>;
      jsExtractorResults?: JsExtractorPreview[];
    }) => {
      if (!payload.html) {
        return;
      }
      setPreviewHtml(payload.html);
      setPreviewUrl(payload.sourceUrl);
      setPreviewUrlInput(payload.sourceUrl);
      setPreviewValues(payload.propertyData || {});
      setJsExtractorResults(payload.jsExtractorResults || []);
      setPreviewError(null);
      setPreviewLoadError(null);
      persistPreview({
        html: payload.html,
        sourceUrl: payload.sourceUrl,
        propertyData: payload.propertyData || {},
        jsExtractorResults: payload.jsExtractorResults || [],
      });
    },
    [persistPreview]
  );

  const handleClearPreview = useCallback(() => {
    previewRequestRef.current += 1;
    setPreviewHtml(null);
    setPreviewUrl(null);
    setPreviewValues({});
    setJsExtractorResults([]);
    setPreviewError(null);
    setPreviewLoading(false);
    setPreviewLoadError(null);
    clearPersistedPreview();
  }, [clearPersistedPreview]);

  const handleLoadPreviewData = useCallback(
    async (urlToLoad: string) => {
      const trimmedUrl = urlToLoad.trim();
      if (!trimmedUrl) {
        setPreviewLoadError('Enter a URL before loading data.');
        return;
      }

      let parsedUrl: URL;
      let normalizedUrl = trimmedUrl;
      try {
        if (/^https?:\/\//i.test(trimmedUrl)) {
          parsedUrl = new URL(trimmedUrl);
        } else {
          parsedUrl = new URL(`https://${trimmedUrl}`);
          normalizedUrl = parsedUrl.href;
        }
      } catch (error) {
        setPreviewLoadError('Enter a valid URL (including protocol).');
        return;
      }

      if (normalizedSourceDomain) {
        const host = parsedUrl.hostname.toLowerCase();
        const matchesDomain =
          host === normalizedSourceDomain ||
          host.endsWith(`.${normalizedSourceDomain}`);
        if (!matchesDomain) {
          setPreviewLoadError(
            `Preview URL must be on ${normalizedSourceDomain}.`
          );
          return;
        }
      }

      setPreviewLoadError(null);
      setPreviewLoadLoading(true);
      try {
        previewRequestRef.current += 1;
        const response = await previewSelectors({
          url: normalizedUrl,
          useBrowser: source.searchResultsConfig?.useBrowser === true,
          cachedAIData,
        });
        if (!response.html) {
          throw new Error('No HTML content returned from the requested URL.');
        }
        setPreviewHtml(response.html);
        setPreviewUrl(normalizedUrl);
        setPreviewUrlInput(normalizedUrl);
        setPreviewValues({});
        setJsExtractorResults(response.jsExtractorResults || []);
        setPreviewError(null);
        if (response.aiFields) {
          setCachedAIData(response.aiFields);
        }
        persistPreview({
          html: response.html,
          sourceUrl: normalizedUrl,
          propertyData: {},
          jsExtractorResults: response.jsExtractorResults || [],
        });
      } catch (error: any) {
        console.error('Failed to load preview data', error);
        setPreviewLoadError(
          error?.response?.data?.message ||
            error?.message ||
            'Unable to load data. The website may have blocked the request or an unexpected error occurred.'
        );
      } finally {
        setPreviewLoadLoading(false);
      }
    },
    [normalizedSourceDomain, persistPreview]
  );

  const handleManualPreviewRefresh = useCallback(() => {
    if (!previewHtml) return;
    setPreviewRefreshToken((token) => token + 1);
  }, [previewHtml]);

  useEffect(() => {
    if (!previewHtml) {
      return;
    }

    const debounceHandle = setTimeout(() => {
      const requestId = previewRequestRef.current + 1;
      previewRequestRef.current = requestId;
      setPreviewLoading(true);
      previewSelectors({
        html: previewHtml,
        selectors: selectorsRef.current,
        js: jsConfigsRef.current,
        cachedAIData,
      })
        .then((response) => {
          if (previewRequestRef.current !== requestId) {
            return;
          }
          setPreviewValues(response.propertyData || {});
          setJsExtractorResults(response.jsExtractorResults || []);
          setPreviewError(null);
          if (response.aiFields) {
            setCachedAIData(response.aiFields);
          }
        })
        .catch((err: any) => {
          if (previewRequestRef.current !== requestId) {
            return;
          }
          console.error('Failed to generate preview:', err);
          const message =
            err.response?.data?.message ||
            err.message ||
            'Failed to generate preview';
          setPreviewError(message);
        })
        .finally(() => {
          if (previewRequestRef.current === requestId) {
            setPreviewLoading(false);
          }
        });
    }, 400);

    return () => {
      clearTimeout(debounceHandle);
    };
  }, [previewHtml, previewRefreshToken]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Remove empty selectors, but preserve those with value mappings
      const cleanedSelectors: Record<string, ScraperSelector> = {};
      Object.entries(selectors).forEach(([key, config]) => {
        // Keep selector if it has a selector value
        if (config.selector?.trim()) {
          cleanedSelectors[key] = config;
        }
      });

      const cleanedJsConfigs: ScraperJsConfig[] = jsConfigs
        .map((config) => ({
          selector: config.selector?.trim() || '',
          function: config.function?.trim() || undefined,
        }))
        .filter((config) => config.selector);

      await updateSource(sourceId, {
        name: source.name,
        sourceType: source.sourceType,
        sourceUrl: source.sourceUrl,
        searchResultsConfig: source.searchResultsConfig,
        urlFilterPattern: source.urlFilterPattern || '',
        keepQueryParams: source.keepQueryParams || '',
        description: source.description,
        scrapingSchedule: source.scrapingSchedule || '',
        agencyId: source.agencyId,
        domain: source.domain,
        selectors: cleanedSelectors,
        js: cleanedJsConfigs,
      });

      setSuccess('Scraper configuration saved successfully');
      onSave();
    } catch (error) {
      console.error('Error saving scraper configuration:', error);
      setError('Failed to save scraper configuration');
    } finally {
      setLoading(false);
    }
  };

  const applyJsonConfiguration = useCallback(
    (config: any) => {
      if (!config || typeof config !== 'object' || Array.isArray(config)) {
        throw new Error('Invalid configuration format');
      }

      if ('selectors' in config || 'js' in config) {
        setSelectors(config.selectors || {});
        setJsConfigs(Array.isArray(config.js) ? config.js : []);
      } else {
        setSelectors(config);
        setJsConfigs([]);
      }
    },
    [setSelectors, setJsConfigs]
  );

  const handleOpenJsonDialog = () => {
    // Create a JSON representation of the selectors and JS configs
    setJsonConfig(JSON.stringify({ selectors, js: jsConfigs }, null, 2));
    setJsonDialogOpen(true);
    setJsonError(null);
  };

  const handleCopyJson = () => {
    navigator.clipboard
      .writeText(jsonConfig)
      .then(() => {
        setSuccess('Configuration copied to clipboard');
        setTimeout(() => setSuccess(null), 3000);
      })
      .catch((err) => {
        console.error('Failed to copy configuration:', err);
        setError('Failed to copy configuration to clipboard');
      });
  };

  const handlePasteJson = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      try {
        const parsedConfig = JSON.parse(clipboardText);
        applyJsonConfiguration(parsedConfig);
        setJsonDialogOpen(false);
        setSuccess('Selectors imported successfully');
      } catch (e) {
        setJsonError('Invalid JSON format');
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      setJsonError('Failed to read from clipboard');
    }
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonConfig(e.target.value);
    setJsonError(null);
  };

  const handleApplyJson = () => {
    try {
      const parsedConfig = JSON.parse(jsonConfig);
      applyJsonConfiguration(parsedConfig);
      setJsonDialogOpen(false);
      setSuccess('Selectors applied successfully');
    } catch (e) {
      setJsonError('Invalid JSON format');
    }
  };

  return (
    <Card className="p-6">
      {error && (
        <Alert variant="error" className="mb-6">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-6">
          {success}
        </Alert>
      )}

      <ScraperConfigFields
        selectors={selectors}
        jsConfigs={jsConfigs}
        onSelectorsChange={handleSelectorsChange}
        onJsConfigsChange={handleJsConfigsChange}
        previewValues={previewValues}
        jsExtractorResults={jsExtractorResults}
        previewHtml={previewHtml}
        previewSourceUrl={previewUrl}
        previewLoading={previewLoading}
        previewError={previewError}
        previewUrlInput={previewUrlInput}
        onPreviewUrlInputChange={handlePreviewUrlInputChange}
        onLoadPreview={handleLoadPreviewData}
        previewLoadLoading={previewLoadLoading}
        previewLoadError={previewLoadError}
        onClearPreview={handleClearPreview}
        onPreviewRefresh={handleManualPreviewRefresh}
        onTestUrl={() => setTestDialogOpen(true)}
      />

      <div className="mt-6 flex justify-between">
        <div>
          <Button
            variant="outline"
            onClick={handleOpenJsonDialog}
          >
            <Code className="h-4 w-4 mr-2" />
            JSON Config
          </Button>
        </div>

        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? <Spinner size="sm" className="mr-2" /> : null}
          {loading ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      <TestUrlDialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        sourceId={sourceId}
        onPreviewReady={handlePreviewReady}
        initialTestUrl={previewUrl || undefined}
      />

      {/* JSON Configuration Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="2xl"
        title={
          <div className="flex justify-between items-center">
            <span>Scraper Selectors JSON</span>
            <div className="flex gap-2">
              <button
                onClick={handleCopyJson}
                className="p-2 rounded hover:bg-gray-100 transition-colors"
                title="Copy to clipboard"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button
                onClick={handlePasteJson}
                className="p-2 rounded hover:bg-gray-100 transition-colors"
                title="Paste from clipboard"
              >
                <ClipboardPaste className="h-4 w-4" />
              </button>
            </div>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setJsonDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleApplyJson}>
              Apply
            </Button>
          </div>
        }
      >
        {jsonError && (
          <Alert variant="error" className="mb-4">
            {jsonError}
          </Alert>
        )}
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
          value={jsonConfig}
          onChange={handleJsonChange}
          rows={20}
        />
      </Dialog>
    </Card>
  );
};

export default ScraperConfigForm;
